// shadcn/ui component: https://ui.shadcn.com/docs/components/radix/sheet
import React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import CloseIcon from '@mui/icons-material/Close';

import { cn } from 'src/component/ds/utils';
import { DsThemedPortal } from 'src/component/ds/DsRoot';

/*
 * Deviations from shadcn:
 * - no SheetOverlay: the page behind stays visible and clickable (no dim, no blur)
 * - non-modal by default, and outside clicks don't close it -- Esc and the X do
 * - left/right sides only
 * - `docked` renders in the layout instead of a portal, beside the content
 */

function Sheet ({ modal = false, ...props }: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root modal={modal} {...props} />;
}

const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  side?: 'left' | 'right';
  docked?: boolean;
  showCloseButton?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ className, children, side = 'right', docked = false, showCloseButton = true, ...props }, ref) => {
  const content = (
    <SheetPrimitive.Content
      ref={ref}
      onInteractOutside={(e) => e.preventDefault()}
      className={cn(
        'ds-flex ds-flex-col ds-gap-4 ds-bg-background ds-text-foreground ds-border-border',
        side === 'right' ? 'ds-border-l' : 'ds-border-r',
        docked && 'ds-relative ds-h-full ds-w-[420px] ds-shrink-0 ds-overflow-y-auto',
        !docked && [
          'ds-fixed ds-inset-y-0 ds-z-50 ds-h-full ds-w-full ds-narrow:ds-w-[420px] ds-shadow-lg ds-overflow-y-auto',
          'ds-transition ds-ease-in-out data-[state=open]:ds-animate-in data-[state=open]:ds-duration-500',
          side === 'right' && 'ds-right-0 data-[state=open]:ds-slide-in-from-right',
          side === 'left' && 'ds-left-0 data-[state=open]:ds-slide-in-from-left'
        ],
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <SheetPrimitive.Close
          className={cn(
            'ds-absolute ds-right-4 ds-top-5 ds-rounded ds-opacity-70 ds-transition-opacity',
            'hover:ds-opacity-100 focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus',
            'disabled:ds-pointer-events-none'
          )}
        >
          <CloseIcon sx={{ fontSize: 16 }} aria-hidden='true' />
          <span className='ds-sr-only'>Close</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  );

  if (docked) {
    return content;
  }

  return (
    <SheetPrimitive.Portal>
      <DsThemedPortal>{content}</DsThemedPortal>
    </SheetPrimitive.Portal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

function SheetHeader ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ds-flex ds-flex-col ds-gap-1.5 ds-p-4', className)} {...props} />;
}

function SheetFooter ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ds-mt-auto ds-flex ds-flex-col ds-gap-2 ds-p-4', className)} {...props} />;
}

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('ds-font-heading ds-text-chemelex-h4 ds-font-medium ds-text-foreground', className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('ds-text-sm ds-text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription
};
